import { Fragment, useEffect, useMemo, useState } from 'react';
import { Terminal, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { listGlobalDeliveries, type WebhookDelivery } from '../../lib/webhooks-api';
import { Button, Badge } from '../../components/ui';
import styles from './admin.module.css';

interface SiteLite { id: string; name: string; slug: string }

// Cross-site webhook + email delivery log (super-admin). Its own admin tab so
// it isn't buried under a form; filterable by site / event / status.
export function AdminLogs() {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        listGlobalDeliveries(),
        apiFetch<{ sites: SiteLite[] }>('/api/admin/sites'),
      ]);
      setDeliveries(d);
      setSites(s.sites);
    } catch {
      setError('load-failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const siteName = useMemo(() => {
    const m = new Map(sites.map((s) => [s.id, s.name]));
    return (id: string | null) => (id ? m.get(id) ?? id.slice(0, 8) : '—');
  }, [sites]);

  const rows = deliveries.filter(
    (d) =>
      (!siteFilter || d.siteId === siteFilter) &&
      (!eventFilter || d.event === eventFilter) &&
      (!statusFilter || d.status === statusFilter),
  );

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Terminal size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>Webhook &amp; Email Deliveries</h1>
          <p className={styles.subtitle}>
            Cross-site webhook payloads, delivery audit history, and email transmission outcomes.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => void load()}
          className="gap-2 h-9 px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </Button>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      {/* Filter Toolbar */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
        <label className={styles.field} style={{ minWidth: 160 }}>
          <span className={styles.label}>Site</span>
          <select className={styles.select} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} style={{ minHeight: 38, height: 38, padding: '4px 10px' }}>
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className={styles.field} style={{ minWidth: 160 }}>
          <span className={styles.label}>Event</span>
          <select className={styles.select} value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} style={{ minHeight: 38, height: 38, padding: '4px 10px' }}>
            <option value="">All events</option>
            <option value="visitor.identified">visitor.identified</option>
            <option value="session.ended">session.ended</option>
            <option value="ticket.created">ticket.created</option>
            <option value="ticket.updated">ticket.updated</option>
          </select>
        </label>
        <label className={styles.field} style={{ minWidth: 120 }}>
          <span className={styles.label}>Status</span>
          <select className={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minHeight: 38, height: 38, padding: '4px 10px' }}>
            <option value="">All</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
            <option value="skipped">skipped</option>
          </select>
        </label>
      </div>

      <section className={styles.section} style={{ padding: 0, border: 'none', background: 'transparent' }}>
        {loading ? (
          <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>Loading…</p>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>
            <Terminal size={32} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>No matching deliveries</div>
            <div className={styles.emptyDescription}>No log entries match the selected filters.</div>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>When</th><th>Site</th><th>Event</th><th>Kind</th><th>Status</th><th>HTTP</th><th>Target</th></tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <Fragment key={d.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === d.id ? null : d.id)}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(d.createdAt).toLocaleString()}</td>
                      <td>{siteName(d.siteId)}</td>
                      <td><span className={styles.badgeMuted}>{d.event}</span></td>
                      <td>{d.kind}</td>
                      <td>
                        {d.status === 'success' ? (
                          <Badge variant="outline" className="text-[11px] text-emerald-500 border-emerald-500/20 bg-emerald-500/10 font-normal">
                            success
                          </Badge>
                        ) : d.status === 'failed' ? (
                          <Badge variant="outline" className="text-[11px] text-red-500 border-red-500/20 bg-red-500/10 font-normal">
                            failed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[11px] text-muted-foreground font-normal">
                            {d.status}
                          </Badge>
                        )}
                      </td>
                      <td>{d.responseStatus ?? '—'}</td>
                      <td className={styles.mono} style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.target ?? ''}>
                        {d.target ?? '—'}
                      </td>
                    </tr>
                    {expanded === d.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--inic-surface-2, rgba(0,0,0,0.02))' }}>
                          {d.error && <p style={{ margin: '0 0 var(--inic-space-2)', color: 'var(--inic-danger, #b91c1c)', fontSize: 'var(--inic-size-xs)' }}>error: {d.error}</p>}
                          {d.requestBody && (
                            <>
                              <span className={styles.label}>Request</span>
                              <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{d.requestBody}</pre>
                            </>
                          )}
                          {d.responseSnippet && (
                            <>
                              <span className={styles.label}>Response</span>
                              <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{d.responseSnippet}</pre>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
